import { useCallback, useMemo } from 'react'

import { useAtom, useAtomValue } from 'jotai'

import {
  connectionDropPreviewNodeAtom,
  nodesAtom,
  showValidationResultsAtom,
  useResolvedNodeDefinitions,
  validationResultsAtom
} from 'Automations/automationEditor/atoms'
import { AutomationsPresentationV1Node } from 'Automations/automationEditor/types'
import { NodeType, ResolvedNodeDefinition } from '../types'
import { getNameForNode, ValidationResult } from '..'

type UseNode = <Config = unknown>(params: {
  nodeId: string
}) => {
  node: AutomationsPresentationV1Node | undefined
  nodeData: Config | undefined
  setNodeData: <K extends keyof Config>(key: K, value: Config[K]) => void
  updateNodeType: (
    targetType: NodeType,
    dataOverrides?: Record<string, unknown>
  ) => void
  name: string
  definition: ResolvedNodeDefinition<Record<string, unknown>> | undefined
  validationErrors: Record<string, ValidationResult[]>
}

export const useNode: UseNode = <Config = unknown>({
  nodeId
}: {
  nodeId: string
}) => {
  const [nodes, setNodes] = useAtom(nodesAtom)
  const previewNode = useAtomValue(connectionDropPreviewNodeAtom)
  const validationResults = useAtomValue(validationResultsAtom)
  const showValidationResults = useAtomValue(showValidationResultsAtom)
  const definitions = useResolvedNodeDefinitions()

  const node = useMemo(() => {
    // The connection-drop preview renders a real editor node for a ghost id
    // that isn't in the presentation; surface its default config here.
    if (previewNode?.id === nodeId) return previewNode
    return nodes.find(n => n.id === nodeId)
  }, [nodes, nodeId, previewNode])

  const nodeData = useMemo(() => {
    return node?.data as Config | undefined
  }, [node])

  const setNodeData = useCallback(
    <K extends keyof Config>(key: K, value: Config[K]) => {
      const updatedNodes = nodes.map(n => {
        if (n.id !== nodeId) return n
        return { ...n, data: { ...n.data, [key]: value } }
      })

      setNodes(updatedNodes)
    },
    [nodeId, nodes, setNodes]
  )

  const updateNodeType = useCallback(
    (targetType: NodeType, dataOverrides?: Record<string, unknown>) => {
      if (!node) return

      const updatedNodes = nodes.map(n => {
        if (n.id !== nodeId) return n

        return {
          ...n,
          type: targetType,
          data: {
            ...definitions[targetType].defaultConfig,
            name: node.data.name,
            ...dataOverrides
          }
        }
      })

      setNodes(updatedNodes)
    },
    [node, nodeId, setNodes, nodes, definitions]
  )

  const name = useMemo(
    () => getNameForNode(node, definitions),
    [node, definitions]
  )

  const definition = useMemo(() => {
    if (!node?.type) return undefined
    return definitions[node.type as NodeType]
  }, [node, definitions])

  const validationErrors = useMemo(() => {
    const nodeValidation = validationResults[nodeId]

    if (!nodeValidation || !showValidationResults) {
      return {} as Record<string, ValidationResult[]>
    }

    return nodeValidation as Record<string, ValidationResult[]>
  }, [validationResults, nodeId, showValidationResults])

  return {
    node,
    nodeData,
    setNodeData,
    updateNodeType,
    name,
    definition,
    validationErrors
  }
}
